import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { ScheduleEditor } from '@/components/ScheduleEditor';
import { ParametersEditor } from '@/components/ParametersEditor';
import { KtInsightsPagePicker } from '@/components/KtInsightsPagePicker';
// The estate's own filter panel, shared with the KT Insights page rather than reimplemented here.
import { KtInsightsFiltersEditor } from '@/components/KtInsightsFiltersEditor';
import { HeadOfficePicker } from '@/components/HeadOfficePicker';
import type { ChosenHeadOffice } from '@/components/HeadOfficePicker';
import { ALL_MONTHS, placementsNeedingAttention } from '@/types';
import type {
  KtInsightsDateRange,
  ReportPackDeliveryMode,
  ReportPackEligibility,
  ReportPackEligibilityFinding,
  ReportPackEnrolmentDto,
  ReportPackItemDto,
  ReportPackRule,
  ReportPackUnenrolResult,
  ReportParameterValue,
  SaveReportPackRequest,
  ScheduleDefinition
} from '@/types';

/** PBIRS rendering-extension ids; the same list the create and detail subscription pages carry. */
const RENDER_FORMATS = [
  { value: 'EXCELOPENXML', label: 'Excel (.xlsx)' },
  { value: 'PDF', label: 'PDF' },
  { value: 'WORDOPENXML', label: 'Word (.docx)' },
  { value: 'CSV', label: 'CSV' },
  { value: 'MHTML', label: 'Web archive (MHTML)' },
  { value: 'XML', label: 'XML' },
  { value: 'IMAGE', label: 'Image (TIFF)' }
];

const DATE_RANGES: { value: KtInsightsDateRange; label: string }[] = [
  { value: 'PreviousMonth', label: 'Previous month' },
  { value: 'PreviousQuarter', label: 'Previous quarter' },
  { value: 'Last7Days', label: 'Last 7 days' },
  { value: 'Last30Days', label: 'Last 30 days' },
  { value: 'Last90Days', label: 'Last 90 days' },
  { value: 'YearToDate', label: 'Year to date' },
  { value: 'None', label: 'No date filter' }
];

/**
 * Minutes between one item and the next, mirroring `ReportPackApplicationService.ItemStaggerMinutes`.
 *
 * Shown here, never edited here. The stagger is derived from the item's position when the pack is
 * applied, so this constant only exists so the editor can say what the derived value will be — the
 * server does not read it, and a number typed on the form would go stale the moment somebody
 * reordered the list.
 */
const ITEM_STAGGER_MINUTES = 5;

/**
 * Where a new pack's schedule starts: monthly, early in the month, after the warehouse load.
 *
 * One schedule for the whole pack — there is deliberately no per-item override. Items are staggered
 * by {@link ITEM_STAGGER_MINUTES} per position at apply time, and apply adds a per-customer spread
 * on top, so a pack of eight applied to forty customers does not put 320 subscriptions on one
 * minute.
 */
const defaultPackSchedule: ScheduleDefinition = {
  frequency: 'Monthly',
  timeOfDay: '07:00:00',
  timeZoneId: 'Europe/London',
  dayOfMonth: 3,
  months: ALL_MONTHS,
  intervalDays: null,
  daysOfWeek: null,
  monthInterval: null,
  startDate: null,
  endDate: null,
  cronExpression: null,
  humanReadable: null
};

/** Everything this form holds. Flattened out of ReportPackDetail: the audit fields are read-only. */
type Draft = {
  name: string;
  description: string;
  isEnabled: boolean;
  deliveryMode: ReportPackDeliveryMode;
  packEmail: string;
  schedule: ScheduleDefinition;
  items: ReportPackItemDto[];
};

const emptyDraft: Draft = {
  name: '',
  description: '',
  isEnabled: false,
  deliveryMode: 'Pickup',
  packEmail: '',
  schedule: defaultPackSchedule,
  items: []
};

export function ReportPackEditor() {
  const api = useApi();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { packId } = useParams<{ packId: string }>();

  const [draft, setDraft] = useState<Draft>(emptyDraft);
  // Which item's full form is open. One at a time: the sections are tall, and two open at once
  // makes the ordering — the thing this screen is mostly about — impossible to see.
  const [editing, setEditing] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: pack, isLoading, error } = useQuery({
    queryKey: ['pack', packId],
    queryFn: () => api.getPack(packId!),
    enabled: Boolean(packId),
    retry: false
  });

  /**
   * How many of this pack's placements want a human to look at them, across every customer.
   *
   * The only screen that can answer it — the library deliberately does not, because it would be one
   * request per row. Failing this query is not failing the page: it costs the "needs attention"
   * line and nothing else, so it must not stop the pack being edited.
   */
  const { data: enrolments, error: enrolmentsError } = useQuery({
    queryKey: ['pack-enrolments', packId],
    queryFn: () => api.packEnrolments(packId!),
    enabled: Boolean(packId),
    retry: false
  });

  // Seeded once per loaded pack rather than on every render, or typing would be overwritten by the
  // last response every time react-query refetched in the background.
  const [seeded, setSeeded] = useState<string | null>(null);
  useEffect(() => {
    if (!pack || seeded === pack.id) return;
    setSeeded(pack.id);
    setDraft({
      name: pack.name,
      description: pack.description ?? '',
      isEnabled: pack.isEnabled,
      deliveryMode: pack.deliveryMode,
      packEmail: pack.packEmail ?? '',
      schedule: pack.schedule,
      items: [...pack.items].sort((a, b) => a.ordinal - b.ordinal)
    });
  }, [pack, seeded]);

  const save = useMutation({
    mutationFn: (req: SaveReportPackRequest) =>
      packId ? api.updatePack(packId, req).then(() => ({ id: packId })) : api.createPack(req),
    onSuccess: result => {
      void qc.invalidateQueries({ queryKey: ['packs'] });
      void qc.invalidateQueries({ queryKey: ['pack', result.id] });
      if (!packId) navigate(`/admin/packs/${result.id}`);
      else setSaved(true);
    }
  });

  // The "Saved" banner is only true of the draft it was shown for. Any edit after it makes it a
  // statement about something that is no longer on screen, so it goes the moment one happens.
  useEffect(() => setSaved(false), [draft]);

  const attention = useMemo(
    () => (enrolments ?? []).reduce((sum, e) => sum + placementsNeedingAttention(e), 0),
    [enrolments]
  );

  if (packId && isLoading) return <div className="placeholder">Loading pack...</div>;
  if (packId && error) {
    return <div className="error-banner">Could not load this pack: {(error as Error).message}</div>;
  }

  const setItem = (id: string, changes: Partial<ReportPackItemDto>) =>
    setDraft(d => ({ ...d, items: d.items.map(i => (i.id === id ? { ...i, ...changes } : i)) }));

  const removeItem = (id: string) =>
    setDraft(d => ({ ...d, items: d.items.filter(i => i.id !== id) }));

  /** Moves an item one place. `to` outside the list is a no-op, so the ends need no guard here. */
  const moveItem = (from: number, to: number) => setDraft(d => {
    if (to < 0 || to >= d.items.length) return d;
    const items = [...d.items];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    return { ...d, items };
  });

  const addItem = (item: ReportPackItemDto) => {
    setDraft(d => ({ ...d, items: [...d.items, item] }));
    setEditing(item.id);
  };

  // The pack email is only sent when the pack is set to use one — clearing the radio must clear the
  // address too, or a pack switched back to pickup-only would keep mailing whoever was typed there.
  const emailWanted = draft.deliveryMode === 'PickupAndEmail';
  const emailMissing = emailWanted && draft.packEmail.trim() === '';

  function handleSave() {
    save.mutate({
      name: draft.name.trim(),
      description: draft.description.trim() || null,
      isEnabled: draft.isEnabled,
      deliveryMode: draft.deliveryMode,
      packEmail: emailWanted ? draft.packEmail.trim() : null,
      schedule: draft.schedule,
      // Ordinal is rewritten from the list's own order on every save rather than tracked as the
      // items move. The array IS the order on screen; a separately maintained number is one that
      // can disagree with it — and now that the run-time stagger is derived from the ordinal, a
      // number that disagreed with the list would also silently change when a report runs.
      //
      // Zero-based, matching what the store renumbers to and what the stagger is computed from, so
      // the first item runs on the pack's own time rather than five minutes after it.
      items: draft.items.map((item, index) => ({ ...item, ordinal: index }))
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{packId ? (draft.name || 'Report pack') : 'New report pack'}</h1>
          <div className="sub">
            {packId ? (
              <>
                {/* Only stated once the enrolments have actually been read. "Applied to 0
                    customers" while that request is still in flight would be a false negative on
                    the one number that decides how careful an edit here has to be. */}
                {enrolments && `Applied to ${enrolments.length} customer${enrolments.length === 1 ? '' : 's'}. `}
                Changes here are not pushed to the subscriptions this pack has already created.
              </>
            ) : 'Define the reports once, then apply them to a customer in one pass.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {packId && (
            <button className="btn-secondary" onClick={() => navigate(`/admin/packs/${packId}/apply`)}>
              Apply to a customer
            </button>
          )}
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={save.isPending || draft.name.trim() === '' || emailMissing}
            title={emailMissing ? 'Enter the address every item should be emailed to' : undefined}
          >
            {save.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {attention > 0 && (
        <div className="notice notice-warn">
          <strong>
            {attention} subscription{attention === 1 ? '' : 's'} created from this pack
            {attention === 1 ? ' needs' : ' need'} attention.
          </strong>
          <div>
            {/* Nothing here repairs itself: a person edited that subscription for a reason. This
                says so and leaves the move to a human, the same way pickup drift does. */}
            One or more customers have a subscription that no longer matches this pack, has gone
            from the report server, or could not be checked. Editing the pack does not change them.
          </div>
        </div>
      )}

      {saved && !save.isPending && (
        <div className="notice" style={{ border: '1px solid var(--kt-grey-200)' }}>
          <strong>Saved.</strong>
          <div>
            The pack definition has changed. Subscriptions already created from it are untouched —
            applying the pack again to a customer only adds the items they do not already have.
          </div>
        </div>
      )}

      {save.error && (
        <div className="error-banner">{(save.error as Error).message.replace(/^API \d+: /, '')}</div>
      )}

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>1. Pack details</h3></div>
        <div className="card-body">
          <div className="field">
            <label>Name</label>
            <input
              className="input"
              value={draft.name}
              placeholder="e.g. Standard Monthly Pack"
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            />
          </div>
          <div className="field">
            <label>Description</label>
            <input
              className="input"
              value={draft.description}
              placeholder="What this suite is for, in one line"
              onChange={e => setDraft(d => ({ ...d, description: e.target.value }))}
            />
            <div className="field-hint">Shown under the pack's name in the library.</div>
          </div>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={draft.isEnabled}
              onChange={e => setDraft(d => ({ ...d, isEnabled: e.target.checked }))}
            />
            Available to apply to customers
          </label>
          <div className="field-hint">
            Leave this off while the pack is being put together. A pack that is not available can
            still be edited and saved; it just cannot be applied to anyone.
          </div>
        </div>
      </div>

      {/* One schedule, for the whole pack. Deliberately its own card and deliberately above the
          reports: a pack IS a suite that arrives together, so "when does this pack run" is a
          property of the pack and answered once. A weekly pack, a monthly pack and a quarterly
          pack are three packs — mixing frequencies inside one was expressible before and never
          wanted, and cost four chances to get one decision wrong. */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>2. Schedule</h3>
          <span style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
            One schedule for every report in the pack
          </span>
        </div>
        <div className="card-body">
          <ScheduleEditor value={draft.schedule} onChange={schedule => setDraft(d => ({ ...d, schedule }))} />
          <div className="field-hint" style={{ marginTop: 12 }}>
            Every report in this pack runs on this schedule, staggered by{' '}
            {ITEM_STAGGER_MINUTES} minutes per position in the
            list below so they do not all hit the report server at once. Applying the pack adds a
            further per-customer spread on top, derived from the head office id, so two customers on
            the same pack do not run in the same minute either.
          </div>
          <div className="field-hint">
            If one report genuinely needs a different frequency, it belongs in a different pack —
            apply both, and the customer gets both.
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>3. Reports in this pack</h3>
          <span style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
            Run in this order, {ITEM_STAGGER_MINUTES} minutes apart
          </span>
        </div>
        <div className="card-body">
          {draft.items.length === 0 && (
            <div className="placeholder">
              No reports yet. Add the first one below.
            </div>
          )}

          {draft.items.map((item, index) => (
            <ItemRow
              key={item.id}
              item={item}
              index={index}
              count={draft.items.length}
              open={editing === item.id}
              onToggle={() => setEditing(current => (current === item.id ? null : item.id))}
              onChange={changes => setItem(item.id, changes)}
              onRemove={() => { removeItem(item.id); setEditing(null); }}
              onMove={to => moveItem(index, to)}
            />
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn-secondary btn-sm" onClick={() => addItem(newPbirsItem())}>
              + Add report
            </button>
            <button className="btn-secondary btn-sm" onClick={() => addItem(newKtInsightsItem())}>
              + Add KT Insights export
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>4. Delivery</h3></div>
        <div className="card-body">
          <label className="checkbox-field">
            <input
              type="radio"
              name="pack-delivery"
              checked={draft.deliveryMode === 'Pickup'}
              onChange={() => setDraft(d => ({ ...d, deliveryMode: 'Pickup' }))}
            />
            Report Pickup only
          </label>
          <label className="checkbox-field">
            <input
              type="radio"
              name="pack-delivery"
              checked={emailWanted}
              onChange={() => setDraft(d => ({ ...d, deliveryMode: 'PickupAndEmail' }))}
            />
            Pickup, and email every item to one address
          </label>
          <div className="field" style={{ margin: '10px 0 0' }}>
            <input
              className="input"
              placeholder="name@example.org"
              value={draft.packEmail}
              disabled={!emailWanted}
              onChange={e => setDraft(d => ({ ...d, packEmail: e.target.value }))}
            />
            <div className="field-hint">
              One address for the whole pack. If different reports need different recipients, apply
              the pack and then edit those subscriptions individually.
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header"><h3>5. Ownership</h3></div>
        <div className="card-body">
          {/* No owner field here on purpose. The portal owner is chosen per apply, not per pack —
              it is on the apply request, not on the pack — and a select here would look like it
              decided something it does not. */}
          <div className="field-hint" style={{ marginTop: 0 }}>
            The portal owner — who manages these subscriptions from the portal — is chosen when the
            pack is applied to a customer, and defaults to whoever applies it.
          </div>
          <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', margin: '12px 0 0' }}>
            <strong>On the report server these are always owned by svc_reportportal.</strong>
            <div>
              That is the account MANDW01 sends mail as, and it is deliberately not a person — so a
              subscription never breaks because someone left.
            </div>
          </div>
        </div>
      </div>

      {/* Only on a saved pack: a pack that does not exist yet cannot have been applied to anyone,
          and an empty card there would read as "nobody is on this" rather than "not yet possible". */}
      {packId && (
        <CustomersCard packId={packId} enrolments={enrolments} error={enrolmentsError as Error | null} />
      )}
    </>
  );
}

// ── Customers ───────────────────────────────────────────────────────────────────────────

function formatEnrolledOn(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Who is on this pack, and the two ways to take one of them off.
 *
 * The list itself is the gap this closes: the enrolments were already being fetched for the
 * "needs attention" line and then thrown away, so an admin could enrol a customer and never see
 * that they had.
 *
 * The two actions are deliberately separate buttons rather than one with a checkbox. Removing a
 * customer from a pack and deleting a customer's reports are different decisions with different
 * consequences, and only one of them can be undone by applying the pack again.
 */
function CustomersCard({ packId, enrolments, error }: {
  packId: string;
  enrolments: ReportPackEnrolmentDto[] | undefined;
  error: Error | null;
}) {
  const api = useApi();
  const qc = useQueryClient();

  // What a stop left behind. Held rather than derived from the mutation's own data so it survives
  // the invalidation that follows, and cleared by the next action of either kind.
  const [survivors, setSurvivors] = useState<ReportPackUnenrolResult | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['pack-enrolments', packId] });
    // The library's customer count is computed from these same rows, so it is now wrong too.
    void qc.invalidateQueries({ queryKey: ['packs'] });
  };

  const forget = useMutation({
    mutationFn: (enrolmentId: string) => api.forgetPackEnrolment(packId, enrolmentId),
    onSuccess: () => { setSurvivors(null); refresh(); }
  });

  const stop = useMutation({
    mutationFn: (enrolmentId: string) => api.stopPackEnrolment(packId, enrolmentId),
    onSuccess: result => {
      // A 200 is not a success here. If any subscription survived, the customer is still on the pack
      // and the ones that are still running are the only thing worth saying.
      setSurvivors(result.enrolmentRemoved ? null : result);
      refresh();
    }
  });

  const busy = forget.isPending || stop.isPending;
  const rows = enrolments ?? [];

  function handleForget(enrolment: ReportPackEnrolmentDto) {
    const count = enrolment.placements.length;
    if (!confirm(
      `Remove ${enrolment.headOfficeId} — ${enrolment.headOfficeName} from this pack?\n\n`
      + `Their ${count} subscription${count === 1 ? '' : 's'} keep${count === 1 ? 's' : ''} running `
      + 'exactly as now — only the link to this pack is removed, and they carry on being managed '
      + 'from the usual screens.\n\n'
      + 'The pack will no longer list them, so applying it to them again would create a second set.')) return;
    setSurvivors(null);
    forget.mutate(enrolment.id);
  }

  function handleStop(enrolment: ReportPackEnrolmentDto) {
    const count = enrolment.placements.length;
    if (!confirm(
      `Stop ${enrolment.headOfficeId} — ${enrolment.headOfficeName} and delete `
      + `${count} subscription${count === 1 ? '' : 's'}?\n\n`
      + `Their reports stop. Every subscription this pack created for them is deleted from the `
      + 'report server, and they are taken off the pack. This cannot be undone.\n\n'
      + 'Files already collected in their pickup folder are not touched.')) return;
    setSurvivors(null);
    stop.mutate(enrolment.id);
  }

  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header">
        <h3>Customers</h3>
        <span style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
          Who this pack has been applied to
        </span>
      </div>
      <div className="card-body">
        {error && (
          // Distinct from "nobody is on this pack": one of those is a customer list and the other is
          // a failed request, and rendering an empty table for the second would be a lie.
          <div className="error-banner">
            The customers on this pack could not be read: {error.message.replace(/^API \d+: /, '')}
          </div>
        )}

        {(forget.error || stop.error) && (
          <div className="error-banner">
            {((forget.error ?? stop.error) as Error).message.replace(/^API \d+: /, '')}
          </div>
        )}

        {survivors && (
          <div className="notice notice-warn">
            <strong>
              {survivors.headOfficeId} — {survivors.headOfficeName} is still on this pack.
            </strong>
            <div>
              {survivors.items.filter(i => i.deleted).length} subscription(s) were deleted, but{' '}
              {survivors.items.filter(i => !i.deleted).length} could not be and are still running.
              The customer is deliberately left on the pack so those are still findable from here —
              try again once the reason below is resolved.
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {survivors.items.filter(i => !i.deleted).map(i => (
                <li key={i.subscriptionId}>
                  <strong>{i.itemTitle}</strong>: {i.error ?? 'could not be deleted.'}
                </li>
              ))}
            </ul>
          </div>
        )}

        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Enrolled</th>
              <th>Portal owner</th>
              <th>Subscriptions</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="placeholder">
                  {enrolments
                    ? 'This pack has not been applied to anyone yet.'
                    : 'Reading the customers on this pack...'}
                </td>
              </tr>
            ) : rows.map(enrolment => {
              const attention = placementsNeedingAttention(enrolment);
              return (
                <tr key={enrolment.id}>
                  <td data-label="Customer">
                    <div className="report-name">
                      {enrolment.headOfficeId} — {enrolment.headOfficeName}
                    </div>
                  </td>
                  <td data-label="Enrolled">
                    {formatEnrolledOn(enrolment.enrolledUtc)}
                    <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 2 }}>
                      by {enrolment.enrolledBy}
                    </div>
                  </td>
                  <td data-label="Portal owner">{enrolment.portalOwnerDisplayName}</td>
                  <td data-label="Subscriptions">
                    {enrolment.placements.length}
                    {attention > 0 && (
                      <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 2 }}>
                        {attention} need{attention === 1 ? 's' : ''} attention
                      </div>
                    )}
                  </td>
                  <td data-label="">
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => handleForget(enrolment)}
                      >
                        Remove from pack
                      </button>
                      <button
                        type="button"
                        className="btn-danger btn-sm"
                        disabled={busy}
                        onClick={() => handleStop(enrolment)}
                      >
                        Stop and delete subscriptions
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="field-hint">
          <strong>Remove from pack</strong> leaves every subscription running and only breaks the link
          to this pack. <strong>Stop and delete subscriptions</strong> deletes them, so the customer's
          reports stop.
        </div>
      </div>
    </div>
  );
}

// ── Items ───────────────────────────────────────────────────────────────────────────────

function newPbirsItem(): ReportPackItemDto {
  return {
    id: crypto.randomUUID(),
    // Rewritten from the list's position on save. An item carries no schedule and no offset:
    // the recurrence is the pack's, and the stagger is derived from this number.
    ordinal: 0,
    producer: 'Pbirs',
    title: '',
    reportPath: null,
    renderFormat: 'EXCELOPENXML',
    parameters: [],
    headOfficeParameterName: null,
    headOfficeBinding: 'HeadOfficeId'
  };
}

function newKtInsightsItem(): ReportPackItemDto {
  return {
    id: crypto.randomUUID(),
    ordinal: 0,
    producer: 'KtInsights',
    title: '',
    headOfficeBinding: 'HeadOfficeId',
    ktReportId: null,
    ktReportName: null,
    ktPageOrderIdsCsv: null,
    ktPageLabels: [],
    // headOffice is left null: apply overwrites it with the customer, and a value typed here would
    // read as if the pack pinned a customer.
    ktParameters: { headOffice: null, dateRange: 'PreviousMonth', customFrom: null, customTo: null, filters: [] },
    ktRenderAsEmail: null
  };
}

/**
 * "+10 min" — what this item's position adds to the pack's own time, shown on the collapsed row.
 *
 * Derived from the row's index, never from a stored value, which is the whole point: reordering the
 * list changes this number the moment the arrow is pressed, so what is on screen cannot disagree
 * with what will be created. The per-customer spread is NOT included — that is a fact about the
 * customer, not the pack, and it is stated separately on the apply preview.
 */
function describeStagger(index: number): string {
  const minutes = index * ITEM_STAGGER_MINUTES;
  return minutes === 0 ? 'On the pack time' : `+${minutes} min`;
}

function ItemRow({ item, index, count, open, onToggle, onChange, onRemove, onMove }: {
  item: ReportPackItemDto;
  index: number;
  count: number;
  open: boolean;
  onToggle: () => void;
  onChange: (changes: Partial<ReportPackItemDto>) => void;
  onRemove: () => void;
  onMove: (to: number) => void;
}) {
  const pageCount = item.ktPageOrderIdsCsv
    ? item.ktPageOrderIdsCsv.split(',').filter(Boolean).length
    : 0;

  return (
    <div style={{
      border: '1px solid var(--kt-grey-200)',
      borderRadius: 'var(--radius-sm)',
      marginBottom: 10,
      background: 'var(--kt-white)'
    }}>
      <div style={{ display: 'flex', gap: 16, padding: '14px 16px', alignItems: 'flex-start' }}>
        <div style={{ display: 'grid', gap: 2 }}>
          {/* Arrows rather than drag-and-drop: the order decides run times, and a keyboard user
              has to be able to change it. */}
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={index === 0}
            aria-label={`Move ${item.title || 'this item'} earlier`}
            onClick={() => onMove(index - 1)}>&uarr;</button>
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={index === count - 1}
            aria-label={`Move ${item.title || 'this item'} later`}
            onClick={() => onMove(index + 1)}>&darr;</button>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)' }}>
            {index + 1}. {item.title || <span style={{ color: 'var(--kt-grey-400)' }}>Untitled item</span>}
          </div>
          {item.producer === 'Pbirs' ? (
            <div className="report-path">{item.reportPath ?? 'No report chosen yet'}</div>
          ) : (
            <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 3 }}>
              KT Insights · {item.ktReportName ?? 'No report chosen yet'}
              {pageCount > 0 && ` · ${pageCount} page${pageCount === 1 ? '' : 's'}`}
            </div>
          )}
          {item.producer === 'Pbirs' && item.headOfficeParameterName && (
            <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 3 }}>
              Bound per customer: <span className="chip">{item.headOfficeParameterName}</span>
            </div>
          )}
          {/* Stated on the collapsed row, not only inside the open form: an export that renders as
              an Internal identity is scoped by its head-office filter alone, and that fact should
              not need a click. */}
          {item.producer === 'KtInsights' && item.ktRenderAsEmail && (
            <div style={{ fontSize: '.8rem', color: 'var(--kt-grey-500)', marginTop: 3 }}>
              Renders as <code>{item.ktRenderAsEmail}</code>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
          {item.producer === 'Pbirs' && item.renderFormat && (
            <span className="chip">{renderFormatLabel(item.renderFormat)}</span>
          )}
          <span title="Derived from this item's position in the pack — reorder to change it.">
            {describeStagger(index)}
          </span>
          <button type="button" className="btn-ghost btn-sm" onClick={onToggle}>
            {open ? 'Done' : 'Edit'}
          </button>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--kt-grey-200)', padding: '16px' }}>
          {item.producer === 'Pbirs'
            ? <PbirsItemFields item={item} onChange={onChange} />
            : <KtInsightsItemFields item={item} onChange={onChange} />}

          {/* No schedule control here, and no offset box. The recurrence is the pack's — set once,
              in section 2 — and the minutes this item is shifted by are derived from its position,
              so the only way to change them is to move the item. A number typed here would go
              stale the first time somebody reordered the list, and would then disagree with the
              order they are looking at. */}
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--kt-grey-200)' }}>
            <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)', marginBottom: 8 }}>When it runs</div>
            <div className="field-hint" style={{ marginTop: 0 }}>
              {index === 0
                ? 'First in the list, so it runs on the pack’s own time.'
                : `Number ${index + 1} in the list, so it runs ${index * ITEM_STAGGER_MINUTES} `
                  + 'minutes after the pack’s own time.'}
              {' '}Move it up or down to change that — there is no per-report schedule, and applying
              the pack shifts every item again by this customer's own spread.
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" className="btn-danger btn-sm" onClick={onRemove}>
              Remove from pack
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function renderFormatLabel(value: string): string {
  return RENDER_FORMATS.find(f => f.value === value)?.label.replace(/ \(.*\)$/, '') ?? value;
}

function PbirsItemFields({ item, onChange }: {
  item: ReportPackItemDto;
  onChange: (changes: Partial<ReportPackItemDto>) => void;
}) {
  const api = useApi();
  const [search, setSearch] = useState('');

  const { data: catalog } = useQuery({
    queryKey: ['catalog', search],
    queryFn: () => api.searchCatalog(search || undefined)
  });

  const { data: definitions, isFetching: loadingParameters, error: parametersError } = useQuery({
    queryKey: ['report-parameters', item.reportPath],
    queryFn: () => api.reportParameters(item.reportPath!),
    enabled: Boolean(item.reportPath),
    retry: false
  });

  /**
   * Whether this report meets the pack standard.
   *
   * Asked the moment a report is picked, because everything it can find is a fault in the REPORT
   * that only BI can fix — and the alternative to finding out here is finding out at apply time,
   * one customer at a time, after the items ordered before this one have been created.
   */
  const { data: eligibility, isFetching: checking } = useQuery({
    queryKey: ['pack-report-eligibility', item.reportPath],
    queryFn: () => api.packReportEligibility(item.reportPath!),
    enabled: Boolean(item.reportPath),
    // The answer is a property of the report, and reports do not change while a pack is being
    // written. Re-asking on every re-render would cost MANDW01 a resolve each time.
    staleTime: 5 * 60_000,
    retry: false
  });

  /**
   * Binds the customer parameter from the standard rather than from a guess.
   *
   * The naming standard exists largely for this: when every report calls it `HeadOfficeId`, the
   * portal can find it and the author never has to work out which of nine parameters is the
   * customer. Only ever fills an EMPTY binding, so it cannot overwrite a deliberate choice on a
   * saved pack or on a report that spells it unusually.
   */
  useEffect(() => {
    if (!eligibility?.headOfficeParameterName || item.headOfficeParameterName) return;
    onChange({ headOfficeParameterName: eligibility.headOfficeParameterName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibility?.headOfficeParameterName, item.headOfficeParameterName]);

  // Memoised so it is one stable array per set of pinned values: valuesWithSpecimen below depends
  // on it, and a fresh [] every render would rebuild that on every keystroke elsewhere in the item.
  const values: ReportParameterValue[] = useMemo(() => item.parameters ?? [], [item.parameters]);

  /**
   * A real customer, named ONLY so the report will answer.
   *
   * Nothing about it is saved on the item and nothing about it reaches a subscription — see
   * {@link SpecimenCustomer} for why it has to exist at all, and {@link withoutBoundParameter} for
   * what keeps it out of the pack.
   */
  const [specimen, setSpecimen] = useState<ChosenHeadOffice | null>(null);

  const boundName = item.headOfficeParameterName?.trim() ?? '';

  /**
   * The pinned values with the specimen customer written into the bound parameter, which is what
   * the resolve inside ParametersEditor sends upstream. Without it, every parameter BELOW the head
   * office in the cascade resolves to an empty list for ever.
   */
  const valuesWithSpecimen: ReportParameterValue[] = useMemo(() => {
    if (!boundName || !specimen) return values;

    return [
      ...values.filter(v => v.name.toLowerCase() !== boundName.toLowerCase()),
      { name: boundName, values: [String(specimen.headOfficeId)], fromField: null }
    ];
  }, [values, boundName, specimen]);

  /**
   * Strips the specimen back out before anything is stored.
   *
   * The bound parameter is the customer, filled in per customer at apply time. A specimen id that
   * leaked into the pack would pin every customer's report to whoever happened to be handy while
   * the pack was being written — created, running monthly, returning somebody else's numbers.
   */
  function withoutBoundParameter(next: ReportParameterValue[]): ReportParameterValue[] {
    if (!boundName) return next;
    return next.filter(v => v.name.toLowerCase() !== boundName.toLowerCase());
  }

  /** The report these parameters were seeded for, so re-renders never re-seed over an edit. */
  const seededPath = useRef<string | null>(item.reportPath ?? null);

  /**
   * Seed the report's own pinned defaults the moment its parameters are known, exactly as the
   * create-subscription form does.
   *
   * Not a convenience — a cascade will not resolve without it. On "Any Client Detail (Custom
   * View)" the Country list is computed from BOTH ViewId and UPN, so a form that has pinned
   * neither asks the report for a list it cannot build and gets nothing back. The symptom is a
   * Country box reading "the report offered no values for this", and Head Office and Date Filter
   * below it waiting forever on a parent that never populates.
   *
   * It hid because the reports with a one-parameter cascade are fine without it: /Sales/Scope 3
   * computes CountryCode from User alone, and the identity parameter is answered separately by
   * useResolvedValues, so that report resolves whether or not anything was seeded. This is only
   * visible on a report that needs a second upstream.
   *
   * Only the defaults the report PINS. A parameter whose default is an expression is deliberately
   * left unset so the report keeps working it out on every run — pinning today's answer is exactly
   * the frozen window the apply step now refuses.
   *
   * Keyed on the path so it seeds once per report and never overwrites a saved pack's values on
   * load, nor the author's own edits afterwards.
   */
  useEffect(() => {
    if (!definitions || !item.reportPath || seededPath.current === item.reportPath) return;
    seededPath.current = item.reportPath;
    if (values.length > 0) return;
    onChange({
      parameters: withoutBoundParameter(definitions
        .filter(d => !d.hasDynamicDefault && d.defaultValues.length > 0)
        .map(d => ({ name: d.name, values: d.defaultValues })))
    });
    // onChange and values are deliberately absent: this fires on the report changing, not on
    // every keystroke that edits the parameters it seeded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [definitions, item.reportPath]);

  return (
    <>
      <div className="field">
        <label>Item title</label>
        <input
          className="input"
          value={item.title}
          placeholder="e.g. Any Client Detail — Detail with UDF"
          onChange={e => onChange({ title: e.target.value })}
        />
        <div className="field-hint">
          What this item is called on screen. Worth setting even when it names the report, because
          two items on one pack routinely differ only by their view.
        </div>
      </div>

      <div className="field">
        <label>Report</label>
        {item.reportPath && (
          <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', marginBottom: 10 }}>
            <div className="report-path">{item.reportPath}</div>
          </div>
        )}
        <input
          className="input"
          type="search"
          placeholder="Search the catalogue..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{
          marginTop: 10,
          border: '1px solid var(--kt-grey-200)',
          borderRadius: 'var(--radius-sm)',
          maxHeight: 200,
          overflow: 'auto'
        }}>
          {(catalog ?? []).map(report => (
            <div
              key={report.path}
              style={{
                padding: '10px 12px',
                borderBottom: '1px solid var(--kt-grey-100)',
                background: item.reportPath === report.path ? 'var(--kt-teal-bg)' : undefined,
                cursor: 'pointer'
              }}
              // Changing the report clears the parameters and the binding: a parameter name only
              // means anything within its own report, so carrying them across would pin values on
              // parameters the new report may not have.
              onClick={() => onChange({
                reportPath: report.path,
                title: item.title || report.name,
                parameters: [],
                headOfficeParameterName: null
              })}>
              <div style={{ fontWeight: 600, color: 'var(--kt-green-dark)' }}>{report.name}</div>
              <div className="report-path">{report.path}</div>
            </div>
          ))}
          {(catalog ?? []).length === 0 && <div className="placeholder">No reports match.</div>}
        </div>
      </div>

      {item.reportPath && (
        <Conformance eligibility={eligibility} checking={checking} />
      )}

      <div className="field" style={{ maxWidth: 320 }}>
        <label>Render format</label>
        <select
          className="select"
          value={item.renderFormat ?? 'EXCELOPENXML'}
          onChange={e => onChange({ renderFormat: e.target.value })}>
          {RENDER_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
      </div>

      <div className="field">
        <label>Head office parameter</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select
            className="select"
            style={{ minWidth: 220 }}
            value={item.headOfficeParameterName ?? ''}
            onChange={e => onChange({ headOfficeParameterName: e.target.value || null })}>
            <option value="">Not bound to the customer</option>
            {(definitions ?? []).map(d => (
              <option key={d.name} value={d.name}>
                {d.displayName.trim() || d.name}
              </option>
            ))}
          </select>
          <select
            className="select"
            style={{ minWidth: 200 }}
            value={item.headOfficeBinding}
            disabled={!item.headOfficeParameterName}
            onChange={e => onChange({ headOfficeBinding: e.target.value === 'HeadOfficeName' ? 'HeadOfficeName' : 'HeadOfficeId' })}>
            <option value="HeadOfficeId">receives the head office id</option>
            <option value="HeadOfficeName">receives the head office name</option>
          </select>
        </div>
        <div className="field-hint">
          The one parameter that changes per customer. <strong>Country is not bound this way</strong>{' '}
          — pin it below like any other fixed value, which makes the pack regional and lets the
          preview verify the customer really is in that country rather than assuming it.
          {' '}Which of id or name you pick only decides which the preview prefers when both would
          match: the value actually written is always the one the report's own list holds for this
          customer, so a name-bound parameter that turns out to take ids still gets the id.
        </div>
      </div>

      <div className="field">
        <label>Pinned parameters</label>
        {!item.reportPath ? (
          <div className="placeholder">Choose a report first.</div>
        ) : loadingParameters && !definitions ? (
          <div className="placeholder">Reading the report's parameters...</div>
        ) : parametersError ? (
          // Not the same as "no parameters": saying that would hide parameters the report does
          // have, and every subscription the pack creates would run on the report's own defaults.
          <div className="error-banner">
            The report's parameters could not be read, so they cannot be pinned here. Items created
            from this one would run with whatever the report defaults to.
          </div>
        ) : (
          <>
            <div className="field-hint" style={{ marginTop: 0, marginBottom: 10 }}>
              Values every customer gets — view, products, date filter, date period. Leave a date
              parameter unset if the report works its own window out on each run; pinning today's
              answer freezes it. Anything that should differ per customer is left alone here and set
              when the pack is applied.
            </div>

            <SpecimenCustomer
              parameterName={item.headOfficeParameterName ?? null}
              specimen={specimen}
              onChange={setSpecimen}
            />

            <ParametersEditor
              definitions={definitions ?? []}
              values={valuesWithSpecimen}
              onChange={next => onChange({ parameters: withoutBoundParameter(next) })}
              reportPath={item.reportPath}
            />
          </>
        )}
      </div>
    </>
  );
}

/**
 * Whether the chosen report meets the pack standard, and what to ask BI for if it does not.
 *
 * <b>Everything here is a fault in the report</b>, and none of it can be repaired from the portal —
 * so each finding is rendered as a problem and a fix together, in words an account manager can put
 * straight into a ticket. Stating the problem alone produces a ticket that comes back asking what to
 * do about it.
 *
 * The panel is deliberately loud when a report is blocked. The alternative to seeing this while
 * choosing the report is seeing it at apply time, against a real customer, after the items ordered
 * before it have already been created.
 */
function Conformance({ eligibility, checking }: {
  eligibility?: ReportPackEligibility;
  checking: boolean;
}) {
  if (checking && !eligibility) {
    return <div className="placeholder">Checking this report against the pack standard...</div>;
  }

  if (!eligibility) return null;

  const blocking = eligibility.findings.filter(f => f.severity === 'Blocks');
  const warnings = eligibility.findings.filter(f => f.severity === 'Warns');

  if (blocking.length === 0 && warnings.length === 0) {
    return (
      <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', marginBottom: 14 }}>
        <strong>This report meets the pack standard.</strong>
        {eligibility.headOfficeParameterName && (
          <div>
            The customer will be written into <code>{eligibility.headOfficeParameterName}</code> for
            every head office the pack is applied to.
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {blocking.length > 0 && (
        <div className="error-banner" style={{ marginBottom: 10 }}>
          <strong>
            This report cannot go in a pack yet — {blocking.length} thing
            {blocking.length === 1 ? '' : 's'} to fix on the report itself.
          </strong>
          <div style={{ marginTop: 4 }}>
            Saving will be refused while this is true. None of it can be changed here; each one is a
            change to the report on MANDW01.
          </div>
          {blocking.map((finding, i) => <Finding key={i} finding={finding} />)}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="notice notice-warn" style={{ marginBottom: 14 }}>
          <strong>Worth fixing — the report still works in a pack.</strong>
          {warnings.map((finding, i) => <Finding key={i} finding={finding} />)}
        </div>
      )}
    </>
  );
}

function Finding({ finding }: { finding: ReportPackEligibilityFinding }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div>
        <span className="chip">{RULE_LABELS[finding.rule]}</span>
        {finding.parameter && <> <code>{finding.parameter}</code></>}
      </div>
      <div style={{ marginTop: 4 }}>{finding.problem}</div>
      <div style={{ marginTop: 4, fontSize: '.86rem' }}>
        <strong>Fix:</strong> {finding.fix}
      </div>
    </div>
  );
}

const RULE_LABELS: Record<ReportPackRule, string> = {
  HeadOffice: 'Customer',
  Dates: 'Dates',
  // Deliberately not "Data access", which is Identity's. The two look alike on screen and mean
  // opposite things: Identity is a grant somebody has to make on the data, Credentials is a setting
  // on the report's data source. Confusing them is the mistake the rule exists to stop.
  Credentials: 'Data source',
  Answerable: 'Parameters',
  Identity: 'Data access'
};

/**
 * Names one real customer so the report will resolve the parameters that sit BELOW the head office
 * in its cascade. Nothing here is saved on the pack.
 *
 * A report's dynamic parameter list is produced by a dataset that takes the parameters above it as
 * arguments. Where one of those is the head office — which a pack fills in per customer, at apply
 * time — the list below it can never resolve while the pack is being written. `CustomerId` on
 * /Sales/Scope 3 depends on HeadOfficeId and CountryCode; `DateFilterId` on Any Client Detail
 * (Custom View) depends on HeadOfficeId as well. Both are required and neither has a default, so
 * the report refuses to create the subscription without them — but the editor could only ever say
 * "available once Head Office Id is chosen", which in a pack is never.
 *
 * Naming a specimen makes the report answer, and the answer is normally the same for everybody:
 * CustomerId offers `-1 = <All>` for every head office, DateFilterId offers the same five date
 * bases. So the author picks from a real list, and the literal they pick is pinned for the pack.
 * Where the right value genuinely differs per customer, leave it unset here — the apply step asks
 * the same question again with the real customer in place.
 */
function SpecimenCustomer({ parameterName, specimen, onChange }: {
  parameterName: string | null;
  specimen: ChosenHeadOffice | null;
  onChange: (specimen: ChosenHeadOffice | null) => void;
}) {
  const [picking, setPicking] = useState(false);

  // Nothing to stand in for. Without a bound parameter the cascade has no customer-shaped hole in
  // it, so every list this report offers already resolves.
  if (!parameterName) return null;

  return (
    <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', margin: '0 0 12px' }}>
      {specimen ? (
        <>
          <div style={{ fontSize: '.86rem' }}>
            Lists below are being resolved as though <code>{parameterName}</code> were{' '}
            <strong>{specimen.headOfficeId} — {specimen.name}</strong>.{' '}
            <button className="btn-ghost btn-sm" type="button" onClick={() => onChange(null)}>
              Clear
            </button>
          </div>
          <div className="field-hint" style={{ marginTop: 4 }}>
            This customer is <strong>not</strong> saved on the pack and no subscription will ever
            carry them — they are here only so the report will answer. Every customer still gets
            their own <code>{parameterName}</code> at apply time.
          </div>
        </>
      ) : picking ? (
        <>
          <div style={{ marginBottom: 6, fontSize: '.86rem' }}>
            Pick any customer this pack would be applied to.
          </div>
          <HeadOfficePicker
            maxWidth={420}
            placeholder="Search for a specimen customer..."
            onChoose={chosen => { onChange(chosen); setPicking(false); }}
          />
          <button className="btn-ghost btn-sm" type="button" onClick={() => setPicking(false)}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <div style={{ fontSize: '.86rem' }}>
            Some of this report's parameters sit below <code>{parameterName}</code> in its cascade,
            so they offer no values until a customer is named.{' '}
            <button className="btn-ghost btn-sm" type="button" onClick={() => setPicking(true)}>
              Resolve lists as a specimen customer
            </button>
          </div>
          <div className="field-hint" style={{ marginTop: 4 }}>
            Nothing about the customer you pick is saved — it only makes the report produce the
            lists, so you can pin a real value instead of typing one blind.
          </div>
        </>
      )}
    </div>
  );
}

function KtInsightsItemFields({ item, onChange }: {
  item: ReportPackItemDto;
  onChange: (changes: Partial<ReportPackItemDto>) => void;
}) {
  const api = useApi();
  const reportId = item.ktReportId ?? null;
  const selectedPages = useMemo(
    () => (item.ktPageOrderIdsCsv ?? '').split(',').filter(Boolean).map(Number),
    [item.ktPageOrderIdsCsv]
  );

  // Both share react-query keys with KtInsightsPagePicker, so naming the report and its pages
  // costs no extra round trip — the picker has already asked for exactly these.
  const { data: reports } = useQuery({ queryKey: ['ktinsights-reports'], queryFn: api.ktInsightsReports });
  const { data: pages } = useQuery({
    queryKey: ['ktinsights-report-pages', reportId],
    queryFn: () => api.ktInsightsReportPages(reportId!),
    enabled: reportId != null
  });

  /**
   * Stores the names alongside the ids, which is what the ids alone cannot give back later: a pack
   * opened when KT Insights is unreachable would otherwise show "22 pages" and nothing else.
   */
  function pick(nextReportId: number | null, nextPages: number[]) {
    const report = (reports?.reports ?? []).find(r => r.reportId === nextReportId);
    const labels = (pages?.pages ?? [])
      .filter(p => nextPages.includes(p.pageOrderId))
      .map(p => p.pageName);
    onChange({
      ktReportId: nextReportId,
      ktReportName: report?.reportName ?? (nextReportId === reportId ? item.ktReportName : null),
      ktPageOrderIdsCsv: nextPages.join(',') || null,
      ktPageLabels: labels,
      title: item.title || report?.reportName || ''
    });
  }

  const parameters = item.ktParameters
    ?? { headOffice: null, dateRange: 'PreviousMonth' as KtInsightsDateRange, customFrom: null, customTo: null, filters: [] };

  return (
    <>
      <div className="field">
        <label>Item title</label>
        <input
          className="input"
          value={item.title}
          placeholder="e.g. KT Insights — Travel Spend, all pages"
          onChange={e => onChange({ title: e.target.value })}
        />
      </div>

      <div className="field">
        <label>Report and pages</label>
        <KtInsightsPagePicker reportId={reportId} selected={selectedPages} onChange={pick} />
      </div>

      {/* The head office, shown as a filled-in control rather than left out.
          It used to be mentioned only in the warning box below, and a customer read that absence
          as "I cannot scope this at all" — so the row is rendered where the control would be, and
          says who fills it in. It is disabled because the value genuinely is not the pack's: a
          customer typed here would be overwritten at apply time, which is worse than no box. */}
      <div className="field" style={{ maxWidth: 480 }}>
        <label htmlFor={`kt-headoffice-${item.id}`}>Head office</label>
        <input
          id={`kt-headoffice-${item.id}`}
          className="input"
          readOnly
          disabled
          value="Set per customer when the pack is applied"
        />
        <div className="field-hint">
          Filled in from the customer you apply the pack to, using that customer's own spelling in
          the KT Insights dataset. The preview shows exactly what each export will be filtered to
          before anything is created.
        </div>
      </div>

      <div className="field" style={{ maxWidth: 320 }}>
        <label>Period</label>
        <select
          className="select"
          value={parameters.dateRange}
          onChange={e => onChange({ ktParameters: { ...parameters, dateRange: e.target.value as KtInsightsDateRange } })}>
          {DATE_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        <div className="field-hint">Resolved fresh on every dispatch, not frozen when the pack is saved.</div>
      </div>

      {/* The estate's own filter panel, the same component the KT Insights page uses.
          Without it a pack item could only ever be scoped by customer and period, so an item that
          needed Currency Displayed or Period Type set had to be built on the KT Insights screen and
          could not be part of a pack at all. */}
      <div className="field">
        <label>Report filters</label>
        {reportId === null ? (
          <div className="placeholder">Choose a report first.</div>
        ) : (
          <KtInsightsFiltersEditor
            reportId={reportId}
            value={parameters}
            onChange={ktParameters => onChange({ ktParameters })}
          />
        )}
        <div className="field-hint">
          Applied to every customer this pack is applied to, exactly as pinned here. Leave a filter
          unticked to include every value.
        </div>
      </div>

      <div className="field">
        <label>Renders as</label>
        <input
          className="input"
          type="email"
          value={item.ktRenderAsEmail ?? ''}
          placeholder="Leave blank to use the portal's service identity"
          onChange={e => onChange({ ktRenderAsEmail: e.target.value.trim() || null })}
        />
        <div className="notice notice-warn" style={{ margin: '10px 0 0' }}>
          <strong>The identity an export renders as decides what is IN the file.</strong>
          <div>
            An Internal identity has no row-level filter applied, so the head office is the only
            thing scoping the export — and the head office is set per customer when the pack is
            applied, never here. The preview states this for every KT Insights item before anything
            is created.
          </div>
        </div>
      </div>
    </>
  );
}
